// Nudge map labels apart so they don't overlap, keeping each one close to its anchor.
// nodes: [{ id, ax, ay, w, h, priority }] where (ax, ay) is the label centre and w/h its box size
// returns: [{ id, x, y, hidden, scale, leader }] in the same order. Labels that still collide
// after nudging are drawn smaller (scale < 1) if that fits; failing that they move further out
// as a callout, and leader is the [x, y] where the line from the anchor should end (else null).
// hidden marks a label that found no room at all

// call cb(p, q, ox, oy) for every pair of boxes overlapping by more than eps on both axes
function overlaps(nodes, pad, eps, cb) {
    const sorted = nodes.slice().sort((a, b) => (a.x - a.w / 2) - (b.x - b.w / 2))
    for (let a = 0; a < sorted.length; a++) {
        const p = sorted[a]
        const right = p.x + p.w / 2 + pad
        for (let b = a + 1; b < sorted.length && sorted[b].x - sorted[b].w / 2 < right; b++) {
            const q = sorted[b]
            const ox = (p.w + q.w) / 2 + pad - Math.abs(p.x - q.x)
            const oy = (p.h + q.h) / 2 + pad - Math.abs(p.y - q.y)
            if (ox > eps && oy > eps) cb(p, q, ox, oy)
        }
    }
}

// point on the edge of n's box, when centred at (x, y), nearest its anchor
function leaderEnd(n, x, y) {
    return [
        Math.max(x - n.w / 2, Math.min(x + n.w / 2, n.ax)),
        Math.max(y - n.h / 2, Math.min(y + n.h / 2, n.ay))
    ]
}

function segmentHitsBox(x1, y1, x2, y2, m) {
    let t0 = 0, t1 = 1
    const clip = (d, lo, hi) => {
        if (d === 0) return lo <= 0 && hi >= 0
        const a = lo / d, b = hi / d
        t0 = Math.max(t0, Math.min(a, b))
        t1 = Math.min(t1, Math.max(a, b))
        return t0 < t1
    }
    return clip(x2 - x1, m.x - m.w / 2 - x1, m.x + m.w / 2 - x1) &&
        clip(y2 - y1, m.y - m.h / 2 - y1, m.y + m.h / 2 - y1)
}

function layoutLabels(nodes, { pad = 0.15, maxShift = 3, pull = 0.02, iterations = 200, scales = [0.8, 0.65], maxLeader = 12 } = {}) {
    const ns = nodes.map((n, i) => ({ ...n, i, x: n.ax, y: n.ay, hidden: false }))

    for (let k = 0; k < iterations; k++) {
        for (const n of ns) {
            n.x += (n.ax - n.x) * pull
            n.y += (n.ay - n.y) * pull
        }
        // separate along the axis with the smaller overlap, half each
        overlaps(ns, pad, 0, (p, q, ox, oy) => {
            if (oy <= ox) {
                const s = (p.y - q.y || p.i - q.i) > 0 ? 1 : -1
                p.y += s * oy / 2
                q.y -= s * oy / 2
            } else {
                const s = (p.x - q.x || p.i - q.i) > 0 ? 1 : -1
                p.x += s * ox / 2
                q.x -= s * ox / 2
            }
        })
        for (const n of ns) {
            n.x = Math.max(n.ax - maxShift, Math.min(n.ax + maxShift, n.x))
            n.y = Math.max(n.ay - maxShift, Math.min(n.ay + maxShift, n.y))
        }
    }

    // whatever still collides: keep the higher priority label, hide the other
    const clashes = new Map()
    overlaps(ns, 0, 0.01, (p, q) => {
        if (!clashes.has(p)) clashes.set(p, [])
        if (!clashes.has(q)) clashes.set(q, [])
        clashes.get(p).push(q)
        clashes.get(q).push(p)
    })
    const kept = new Set()
    const ranked = [...clashes.keys()].sort((a, b) => (b.priority || 0) - (a.priority || 0) || a.i - b.i)
    for (const n of ranked) {
        if (clashes.get(n).some((m) => kept.has(m))) {
            n.hidden = true
        } else {
            kept.add(n)
        }
    }

    // give each hidden label the nearest free spot within maxLeader of its anchor,
    // to be drawn as a callout with a leader line back to the anchor
    const fits = (n, x, y) => ns.every((m) => m === n || m.hidden ||
        Math.abs(x - m.x) >= (n.w + m.w) / 2 + pad || Math.abs(y - m.y) >= (n.h + m.h) / 2 + pad)
    // how many other labels the leader from n's anchor to its box at (x, y) runs through
    const crossings = (n, x, y) => {
        const [ex, ey] = leaderEnd(n, x, y)
        return ns.filter((m) => m !== n && !m.hidden && segmentHitsBox(n.ax, n.ay, ex, ey, m)).length
    }
    // spots on rings around n's anchor, nearest first
    function* rings(n, from, to) {
        for (let r = from; r <= to; r += n.h / 4) {
            const steps = Math.max(1, Math.ceil(2 * Math.PI * r / (n.h / 2)))
            for (let s = 0; s < steps; s++) {
                const a = 2 * Math.PI * s / steps
                yield [n.ax + r * Math.cos(a), n.ay + r * Math.sin(a)]
            }
        }
    }

    // first try the label in smaller type, still within maxShift of its anchor
    const full = new Map(ranked.map((n) => [n, [n.w, n.h]]))
    shrink:
    for (const n of ranked.filter((m) => m.hidden)) {
        for (const scale of scales) {
            n.w = full.get(n)[0] * scale
            n.h = full.get(n)[1] * scale
            for (const [x, y] of rings(n, 0, maxShift)) {
                if (!fits(n, x, y)) continue
                n.x = x
                n.y = y
                n.hidden = false
                n.scale = scale
                continue shrink
            }
        }
        [n.w, n.h] = full.get(n)
    }

    // otherwise the nearest spot whose leader crosses nothing, else the one crossing the fewest labels
    for (const n of ranked.filter((m) => m.hidden)) {
        let best = null
        for (const [x, y] of rings(n, maxShift, maxLeader)) {
            if (!fits(n, x, y)) continue
            const c = crossings(n, x, y)
            if (!best || c < best.c) best = { x, y, c }
            if (c === 0) break
        }
        if (best) {
            n.x = best.x
            n.y = best.y
            n.hidden = false
            n.leader = true
        }
    }

    return ns.map((n) => ({ id: n.id, x: n.x, y: n.y, hidden: n.hidden, scale: n.scale || 1, leader: n.leader ? leaderEnd(n, n.x, n.y) : null }))
}

export default layoutLabels
